import { connect } from 'react-redux';
import * as classRoomSelectors from '@core/selectors/classroom';
import { updateMaterialProgress } from '@core/materials/operations/updateMaterialProgress.operation';
import {
  isUpdatingMaterialProgress,
  isSectionUserProgressComplete,
} from '@core/materials/selectors';
import {
  canCreateMaterials,
  canEditMaterials,
  canDeleteMaterials,
  isMaterialRbacEnabled,
  canAccessEducatorArea,
} from '@core/materials/permissions';
import { resetAllFileScanStatuses } from '@core/actions/data/fileScan';
import {
  getUserPermissionsInCurrentSpace,
  hasPermission,
  MATERIAL_PERMISSIONS,
} from '@core/utils/permissions';

import SectionView from './SectionView';
import * as appCreators from '../../../../actions/materials';
import { selectors, operations } from '../../../../store/materials';

const mapStateToProps = (state, ownProps) => {
  const sectionId = selectors.currentSectionId(state);
  const isStudent = classRoomSelectors.getIsStudent(state);
  const isRbacEnabled = isMaterialRbacEnabled(state);
  const permissions = getUserPermissionsInCurrentSpace(state);
  const isReadOnlyUser = classRoomSelectors.accessingCurrentSpaceAsStaff(state);

  // In educator area: if user has educator area permission, they can do everything
  // Otherwise, use individual permissions
  const canAccessEA = canAccessEducatorArea(state);
  const canCreate = ownProps.educatorOnly
    ? canAccessEA
    : canCreateMaterials(state);
  const canEdit = ownProps.educatorOnly ? canAccessEA : canEditMaterials(state);
  const canDelete = ownProps.educatorOnly
    ? canAccessEA
    : canDeleteMaterials(state);

  const hasProgressPermission = hasPermission(
    state,
    MATERIAL_PERMISSIONS.PROGRESS,
    false
  );

  // In educator area: if user has educator area permission, always show educator view
  // Otherwise, use normal logic
  let showEducatorView;
  if (ownProps.educatorOnly) {
    showEducatorView = canAccessEA;
  } else if (isStudent) {
    // Student: show editable view if they have any action permission (including history)
    showEducatorView =
      isRbacEnabled &&
      (canCreate ||
        canEdit ||
        canDelete ||
        hasPermission(state, MATERIAL_PERMISSIONS.EXPORT, false) ||
        hasPermission(state, MATERIAL_PERMISSIONS.CREATE, false) ||
        hasPermission(state, MATERIAL_PERMISSIONS.PUBLISH, false) ||
        hasPermission(state, MATERIAL_PERMISSIONS.HISTORY, false));
  } else {
    // Instructor: always show editable view
    showEducatorView = true;
  }

  const section = selectors.currentSection(state);
  // Check if section is effectively hidden (section itself or parent folder is hidden)
  const parentFolder = section?.parent
    ? selectors.getMaterial(state, section.parent)
    : null;
  const isSectionPublished = section
    ? !section.hidden && !parentFolder?.hidden
    : false;

  return {
    sectionId,
    section,
    isEducator: showEducatorView,
    canEditMaterial: canEdit,
    canDeleteMaterial: canDelete,
    isStudent,
    isReadOnlyUser,
    editing: selectors.editing(state),
    saving: selectors.isSavingSection(state),
    selectedContent: selectors.selectedContent(state),
    isLoadingContent: selectors.isLoadingContent(state),
    reverting: selectors.isRevertingSection(state),
    permissions,
    isRbacEnabled,
    isSectionPublished,
    // When RBAC is disabled: track progress for students (legacy behavior)
    // When RBAC is enabled: track progress only if user has progress permission
    isComplete: (() => {
      if (!isRbacEnabled) {
        return isStudent
          ? isSectionUserProgressComplete(state, sectionId)
          : false;
      }
      return hasProgressPermission
        ? isSectionUserProgressComplete(state, sectionId)
        : false;
    })(),
    isCompleteLoading: (() => {
      if (!isRbacEnabled) {
        return isStudent ? isUpdatingMaterialProgress(state, sectionId) : false;
      }
      return hasProgressPermission
        ? isUpdatingMaterialProgress(state, sectionId)
        : false;
    })(),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onDelete: (sectionId) =>
    dispatch(
      operations.deleteSection({
        sectionId,
        selectSection: appCreators.selectSectionInJourney,
        educatorOnly: ownProps.educatorOnly,
      })
    ),
  onGetSection: (sectionId) =>
    dispatch(
      operations.getSection({
        sectionId,
        educatorOnly: ownProps.educatorOnly,
      })
    ),
  onFindPreviousVersion: (sectionId) =>
    dispatch(operations.displayRevertModal(sectionId)),
  onSave: async (patch, correlationId) => {
    await dispatch(
      operations.saveSection({
        patch,
        select: appCreators.selectSectionInJourney,
        educatorOnly: ownProps.educatorOnly,
        correlationId,
      })
    );
    dispatch(resetAllFileScanStatuses());
  },
  onHide: (sectionId) => dispatch(operations.hideMaterial(sectionId)),
  onUnhide: (sectionId) => dispatch(operations.unHideMaterial(sectionId)),
  onEdit: () => dispatch(operations.setEditing(true)),
  onHideDiscussion: () => dispatch(operations.hideDiscussion()),
  openScheduleDialog: (sectionId) =>
    dispatch(operations.openScheduleMaterial(sectionId)),
  onKeepHidden: (sectionId) =>
    dispatch(
      operations.saveSection({
        id: sectionId,
        patch: {
          hidden: true,
          scheduledFor: null,
        },
        select: appCreators.selectSectionInJourney,
      })
    ),
  onExportMaterialPage: () => dispatch(appCreators.exportMaterialPage()),
  onImportMaterialPage: (sectionId, importingContent) =>
    dispatch(appCreators.importMaterialPage(sectionId, importingContent)), // eslint-disable-line
  submitProgress: (sectionId, userProgress) =>
    dispatch(updateMaterialProgress(sectionId, userProgress)),
  resetAllFileScanStatuses: () => dispatch(resetAllFileScanStatuses()),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps,
  onDelete: () => dispatchProps.onDelete(stateProps.sectionId),
  onHide: () => dispatchProps.onHide(stateProps.sectionId),
  onUnhide: () => dispatchProps.onUnhide(stateProps.sectionId),
  onFindPreviousVersion: () =>
    dispatchProps.onFindPreviousVersion(stateProps.sectionId),
  onExportMaterialPage: () =>
    dispatchProps.onExportMaterialPage(stateProps.sectionId),
  onImportMaterialPage: (importingContent) =>
    dispatchProps.onImportMaterialPage(stateProps.sectionId, importingContent),
  submitProgress: (userProgress) =>
    dispatchProps.submitProgress(stateProps.sectionId, userProgress),
  resetAllFileScanStatuses: dispatchProps.resetAllFileScanStatuses,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(SectionView);
